import React from "react";
import { Accordion, AccordionDetails, AccordionSummary, Button } from "@material-ui/core";
import moment from "moment";
import { Download } from "../Export";
import Icon from "./icons";
import { T, TONE, cardShadow } from "./tokens";
import { decodeAnnualIncome, displayValue, maskAccountNumber } from "./maps";

interface Props {
  kyc: any;
  accountDetails: any;
  riskProfile: string;
  mobileNumber: string;
  statusLabel: string;
  submittedText: string;
}

interface Row {
  label: string;
  value: string;
}

const yn = (v: unknown): string => (v ? "Yes" : "No");

function buildSections(p: Props): { title: string; rows: Row[] }[] {
  const kyc = p.kyc || {};
  const name = kyc.name || {};
  const ids = kyc.ids || {};
  const work = kyc.work || {};
  const pep = kyc.PEP || {};
  const fatca = kyc.FATCA || {};
  const cur = kyc.currentAddress || {};
  const perm = kyc.permanentAddress || {};
  const ad = p.accountDetails || {};
  const fullName = [name.firstName, name.middleName, name.lastName, name.suffix]
    .filter((s: string) => s && s !== "")
    .join(" ");
  const line = (a: any) =>
    [a.street, a.city, a.province, a.zip, a.country].filter((s: string) => s && s !== "").join(", ");

  return [
    {
      title: "Personal Information",
      rows: [
        { label: "Full Name", value: displayValue(fullName) },
        { label: "Date of Birth", value: kyc.dateOfBirth ? moment(kyc.dateOfBirth).format("LL") : "\u2014" },
        { label: "Nationality", value: displayValue(kyc.nationality) },
        { label: "Gender", value: displayValue(kyc.gender) },
        { label: "Civil Status", value: displayValue(kyc.civil?.civilStatus) },
        { label: "Place of Birth", value: displayValue(kyc.placeOfBirth) },
        { label: "Email Address", value: displayValue(kyc.email) },
        { label: "Mobile Number", value: displayValue(p.mobileNumber) },
        { label: "SSS/GSIS", value: displayValue(ids.SSS) },
        { label: "TIN", value: displayValue(ids.TIN) },
      ],
    },
    {
      title: "Address Details",
      rows: [
        { label: "Current Address", value: displayValue(line(cur)) },
        { label: "Permanent Address", value: displayValue(line(perm)) },
      ],
    },
    {
      title: "Professional Details",
      rows: [
        { label: "Occupation", value: displayValue(work.primaryOccupation) },
        { label: "Employer", value: displayValue(work.nameOfEmployer) },
        { label: "Nature of Business", value: displayValue(work.businessNature) },
        { label: "Office Tel. No.", value: displayValue(work.businessNum) },
        { label: "Source of Funds", value: displayValue(work.sourceOfFunds) },
      ],
    },
    {
      title: "Financial Profile",
      rows: [{ label: "Annual Gross Income", value: decodeAnnualIncome(work.annualIncomeAmount) }],
    },
    {
      title: "PEP Declaration",
      rows: [
        { label: "Worked in a government agency?", value: yn(pep.governmentWork) },
        { label: "Related to a government official?", value: yn(pep.governmentRelative) },
      ],
    },
    {
      title: "Risk Profile",
      rows: [
        {
          label: "Risk Appetite",
          value: p.riskProfile ? p.riskProfile.charAt(0).toUpperCase() + p.riskProfile.slice(1) : "\u2014",
        },
      ],
    },
    {
      title: "FATCA",
      rows: [
        { label: "US Citizen?", value: yn(fatca.usCitizen) },
        { label: "US Resident?", value: yn(fatca.usResident) },
        { label: "US Resident Alien?", value: yn(fatca.usAlien) },
        { label: "US Telephone Number?", value: yn(fatca.usPhone) },
        { label: "Born in the US?", value: yn(fatca.usBorn) },
        { label: "US Residence Address?", value: yn(fatca.usAddressResidence) },
        { label: "US Mailing Address?", value: yn(fatca.usAddressMailing) },
        { label: "Standing US Account Instruction?", value: yn(fatca.usAccount) },
        { label: "US Power of Attorney?", value: yn(fatca.usPOA) },
        { label: "In-Care-Of US Address?", value: yn(fatca.usCareOfAddress) },
      ],
    },
    {
      title: "Settlement Details",
      rows: [
        { label: "Account Name", value: displayValue(ad.accountName) },
        { label: "Bank Name", value: displayValue(ad.bankName) },
        { label: "Account Number", value: maskAccountNumber(ad.bankAccountNumber) },
        {
          label: "Account Type",
          value:
            ad.savingsAccount === "N/A" || ad.savingsAccount === undefined
              ? "\u2014"
              : ad.savingsAccount
              ? "Savings Account"
              : "Current Account",
        },
      ],
    },
  ];
}

const MyApplicationCard: React.FC<Props> = (props) => {
  if (!props.kyc) {
    return (
      <div style={card}>
        <p style={{ margin: 0, color: T.muted }}>No application on file yet.</p>
      </div>
    );
  }
  const sections = buildSections(props);

  return (
    <div style={card}>
      <div style={{ display: "flex", alignItems: "center", gap: 14 }}>
        <span style={iconTile}>
          <Icon name="list" size={22} color={T.green} />
        </span>
        <div style={{ display: "flex", flexDirection: "column", gap: 3 }}>
          <h3 style={heading}>My Application</h3>
          <p style={{ margin: 0, fontSize: 13, lineHeight: 1.4, color: T.muted }}>
            Your submitted KYC application, organised by section.
          </p>
        </div>
      </div>

      <div style={{ height: 1, background: T.hairline2, margin: "22px 0 24px" }} />

      <div style={{ display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap", marginBottom: 18 }}>
        <span
          style={{
            display: "inline-flex",
            alignItems: "center",
            gap: 7,
            fontWeight: 700,
            fontSize: 11.5,
            color: TONE.pending.fg,
            background: TONE.pending.bg,
            border: `1px solid ${TONE.pending.bd}`,
            padding: "5px 12px",
            borderRadius: 20,
          }}
        >
          <span style={{ width: 7, height: 7, borderRadius: "50%", background: T.gold, display: "block" }} />
          {props.statusLabel}
        </span>
        <span style={{ fontSize: 12.5, color: T.muted2 }}>{props.submittedText}</span>
        <span style={{ fontSize: 12.5, color: T.muted2, marginLeft: "auto" }}>
          All {sections.length} sections complete
        </span>
      </div>

      <div style={{ border: `1px solid ${T.hairline2}`, borderRadius: 13, overflow: "hidden" }}>
        {sections.map((s, i) => (
          <Accordion
            key={s.title}
            elevation={0}
            square
            style={{ borderBottom: i < sections.length - 1 ? `1px solid ${T.hairline2}` : "none" }}
          >
            <AccordionSummary expandIcon={<Icon name="chevronDown" size={20} color={T.green} />}>
              <span style={{ display: "flex", alignItems: "center", gap: 14, width: "100%" }}>
                <span style={{ width: 30, height: 30, borderRadius: "50%", background: T.green, display: "grid", placeItems: "center", flexShrink: 0 }}>
                  <Icon name="check" size={14} color="#fff" />
                </span>
                <span style={{ flex: 1, fontWeight: 700, fontSize: 14.5, color: T.body }}>{s.title}</span>
              </span>
            </AccordionSummary>
            <AccordionDetails>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "14px 28px", padding: "0 8px 8px 60px", width: "100%" }}>
                {s.rows.map((r) => (
                  <div key={r.label}>
                    <div style={{ fontWeight: 500, fontSize: 11, color: T.muted3, textTransform: "uppercase", letterSpacing: ".04em", marginBottom: 3 }}>
                      {r.label}
                    </div>
                    <div style={{ fontWeight: 600, fontSize: 13.5, color: T.body }}>{r.value}</div>
                  </div>
                ))}
              </div>
            </AccordionDetails>
          </Accordion>
        ))}
      </div>

      <div style={{ marginTop: 20 }}>
        <Download
          data={{ my_application: props.kyc, account_details: props.accountDetails, risk_profile: props.riskProfile }}
          filename="My-KYC-Details"
          button={
            <Button
              startIcon={<Icon name="download" size={17} color="#fff" />}
              style={{
                background: T.green,
                color: "#fff",
                border: `2px solid ${T.gold}`,
                height: 48,
                padding: "0 24px",
                borderRadius: 9,
                fontWeight: 700,
                fontSize: 13.5,
                letterSpacing: ".02em",
              }}
            >
              DOWNLOAD DETAILS
            </Button>
          }
        />
      </div>
    </div>
  );
};

const card: React.CSSProperties = {
  background: "#fff",
  border: `1.5px solid ${T.hairline}`,
  borderRadius: 16,
  padding: "30px 32px",
  boxShadow: cardShadow,
};
const iconTile: React.CSSProperties = {
  width: 46,
  height: 46,
  borderRadius: 12,
  background: T.mint2,
  display: "grid",
  placeItems: "center",
  flexShrink: 0,
};
const heading: React.CSSProperties = {
  margin: 0,
  fontWeight: 700,
  fontSize: 21,
  lineHeight: 1.1,
  color: T.heading,
  letterSpacing: "-.01em",
};

export default MyApplicationCard;
